import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n";
import ownerImg from "../assets/owner.jpg";
import { DotRainbow, Garland, PhotoCard, ScallopDivider, Flower, Cloud } from "./decor";

const CARD_COLORS = ["bg-pPink", "bg-pBlue", "bg-pYellow", "bg-pPeach", "bg-pLavender"];

export default function About() {
  const { s } = useLang();
  return (
    <section id="about" data-testid="about-section" className="relative pt-16 pb-0 overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-4 sm:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="font-display font-bold text-center text-4xl sm:text-5xl lg:text-6xl leading-tight"
        >
          <span className="text-forest">🌈 </span>
          <span className="text-sageBlue">{s.about.h1a}</span>
          <span className="text-coral">{s.about.h1b}</span>
        </motion.h2>
        <p className="font-display font-semibold text-center text-forest text-xl sm:text-2xl mt-4">
          {s.about.welcome}
        </p>

        {/* intro: owner photo + note card */}
        <div className="mt-14 grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="relative w-64 sm:w-80 mx-auto"
          >
            <PhotoCard
              testid="owner-photo"
              src={ownerImg}
              alt={s.about.ownerPhotoAlt}
              color="#A9E2CB"
            />
            <Flower className="absolute -left-3 top-6 w-9 animate-sway" petal="#FFF8F0" center="#FFD97A" />
            <Cloud className="absolute -right-4 bottom-10 w-16 opacity-90" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="relative bg-cardGreen rounded-[2rem] px-7 sm:px-10 pt-20 pb-10 shadow-card lg:mt-16"
            data-testid="about-intro-card"
          >
            <DotRainbow className="absolute -top-12 left-10 w-40 sm:w-48" />
            <Garland className="absolute top-5 left-7 right-7 w-[calc(100%-3.5rem)]" />
            <p className="text-forest/90 leading-relaxed">{s.about.hello}</p>
            <p className="mt-4 text-forest/90 leading-relaxed">{s.about.mom}</p>
            <p className="mt-4 text-forest/90 leading-relaxed">{s.about.degree}</p>
          </motion.div>
        </div>

        {/* themed cards */}
        <div className="mt-16 grid sm:grid-cols-2 gap-6">
          {s.about.sections.map((sec, i) => (
            <motion.div
              key={sec.title}
              data-testid={`about-section-card-${i + 1}`}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: (i % 2) * 0.1 }}
              className={`${CARD_COLORS[i % CARD_COLORS.length]} rounded-3xl p-7 shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-300 ${
                i === s.about.sections.length - 1 ? "sm:col-span-2" : ""
              }`}
            >
              <h3 className="font-display font-semibold text-forest text-xl">
                <span className="mr-1.5">{sec.emoji}</span>
                {sec.title}
              </h3>
              {sec.paras.map((p, j) => (
                <p key={j} className="text-forest/85 text-[15px] leading-relaxed mt-3">{p}</p>
              ))}
            </motion.div>
          ))}
        </div>

        {/* closing */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.6 }}
          className="relative mt-14 max-w-3xl mx-auto text-center rounded-3xl bg-white shadow-soft p-8 sm:p-10"
          data-testid="about-closing"
        >
          <DotRainbow className="w-32 mx-auto -mb-2" />
          <p className="text-forest/85 text-base sm:text-lg leading-relaxed mt-4">{s.about.thanks}</p>
          <p className="font-display font-semibold text-coral text-lg mt-4">{s.about.sign}</p>
        </motion.div>
      </div>

      <div className="mt-16">
        <ScallopDivider fill="#E08D75" />
      </div>
    </section>
  );
}
